import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Building2, Send, ArrowUpRight } from "lucide-react";
import { SectionHeader } from "@/sections/Services";

const PROJECT_TYPES = [
  "Website",
  "Mobile App",
  "Web App / SaaS",
  "Business System",
  "AI Product",
  "Not Sure Yet",
];

const BUDGETS = [
  "Under £2,500",
  "£2,500–£5,000",
  "£5,000–£10,000",
  "£10,000+",
  "Not Sure Yet",
];

const initialState = {
  name: "",
  company: "",
  email: "",
  phone: "",
  projectType: "",
  budget: "",
  message: "",
};

const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT;

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email address";
    if (!form.message.trim()) e.message = "Tell us a little about your project";
    return e;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: undefined });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);

    try {
      if (FORMSPREE_ENDPOINT) {
        const res = await fetch(FORMSPREE_ENDPOINT, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...form,
            _subject: `New enquiry from ${form.name}${form.company ? " — " + form.company : ""}`,
          }),
        });
        if (!res.ok) {
          let detail = "";
          try {
            const j = await res.json();
            detail = (j && (j.error || (j.errors && j.errors[0]?.message))) || "";
          } catch (_) {}
          throw new Error(detail || `Submission failed (${res.status})`);
        }
      } else {
        // Fallback: simulate (no endpoint configured yet)
        await new Promise((r) => setTimeout(r, 700));
      }
      toast.success("Thanks — your enquiry has been received.", {
        description: "We'll be in touch shortly.",
      });
      setForm(initialState);
    } catch (err) {
      toast.error("Something went wrong sending your message.", {
        description:
          "Please try again, or email hello@mackbetech.com directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 border-t border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <SectionHeader
              eyebrow="Contact"
              title="Let's discuss your project"
              subtitle="Share a few details and we'll come back to you with thoughts, suggestions and a proposed next step."
            />

            <div className="mt-10 space-y-4">
              <ContactItem
                icon={Mail}
                label="Email"
                value="hello@mackbetech.com"
                href="mailto:hello@mackbetech.com"
                testid="contact-email"
              />
              <ContactItem
                icon={MapPin}
                label="Location"
                value="400 Thames Valley Park Dr, Earley, Reading, RG6 1PT, United Kingdom"
                href="https://maps.google.com/?q=400+Thames+Valley+Park+Dr,+Earley,+Reading,+RG6+1PT"
                external
                testid="contact-location"
              />
              <ContactItem
                icon={Building2}
                label="Company"
                value="Mackbe Tech Ltd"
                testid="contact-company-info"
              />
            </div>

            {!FORMSPREE_ENDPOINT && (
              <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-[13px] text-slate-400 leading-relaxed">
                This form is currently prepared for integration. It can be connected
                to email, CRM or a custom backend.
              </div>
            )}
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              noValidate
              className="mk-reveal mk-card rounded-3xl p-7 md:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                  label="Name *"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  error={errors.name}
                  testid="contact-name-input"
                />
                <Field
                  label="Company"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  placeholder="Your company"
                  testid="contact-company-input"
                />
                <Field
                  label="Email *"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  error={errors.email}
                  testid="contact-email-input"
                />
                <Field
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+44 ..."
                  testid="contact-phone-input"
                />
                <SelectField
                  label="Project type"
                  name="projectType"
                  value={form.projectType}
                  onChange={handleChange}
                  options={PROJECT_TYPES}
                  placeholder="Select a project type"
                  testid="contact-project-type-select"
                />
                <SelectField
                  label="Budget range"
                  name="budget"
                  value={form.budget}
                  onChange={handleChange}
                  options={BUDGETS}
                  placeholder="Select a budget"
                  testid="contact-budget-select"
                />
              </div>

              <div className="mt-5">
                <Label htmlFor="message">Message *</Label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  className="mk-input mt-2"
                  placeholder="Briefly tell us about your project, goals and timeline."
                  value={form.message}
                  onChange={handleChange}
                  data-testid="contact-message-input"
                />
                {errors.message && <ErrorText text={errors.message} />}
              </div>

              <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-slate-500 max-w-sm">
                  By submitting, you agree to be contacted regarding your enquiry.
                  See our <a href="/privacy" className="mk-underline-link">Privacy Policy</a>.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center gap-2 mk-btn-primary px-7 py-4 rounded-full text-[15px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>Sending…</>
                  ) : (
                    <>
                      Send Enquiry
                      <Send className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactItem({ icon: Icon, label, value, href, external, testid }) {
  const Comp = href ? "a" : "div";
  const linkProps = href
    ? external
      ? { href, target: "_blank", rel: "noopener noreferrer" }
      : { href }
    : {};
  return (
    <Comp
      {...linkProps}
      data-testid={testid}
      className={`flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 ${
        href ? "hover:bg-white/[0.06] hover:border-white/20" : ""
      } transition-all duration-300`}
    >
      <div className="shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-[#07111F]">
        <Icon className="h-4 w-4 text-[#00D4FF]" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <div className="mt-0.5 text-[15px] font-medium text-white leading-snug">{value}</div>
      </div>
      {href && <ArrowUpRight className="ml-auto h-4 w-4 text-slate-500 shrink-0" />}
    </Comp>
  );
}

function Label({ children, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[12px] font-medium uppercase tracking-[0.16em] text-slate-400"
    >
      {children}
    </label>
  );
}

function Field({ label, name, type = "text", value, onChange, placeholder, error, testid }) {
  return (
    <div>
      <Label htmlFor={name}>{label}</Label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="mk-input mt-2"
        data-testid={testid}
      />
      {error && <ErrorText text={error} />}
    </div>
  );
}

function SelectField({ label, name, value, onChange, options, placeholder, testid }) {
  return (
    <div>
      <Label htmlFor={name}>{label}</Label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        className="mk-input mt-2"
        data-testid={testid}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function ErrorText({ text }) {
  return <p className="mt-1.5 text-xs text-rose-400">{text}</p>;
}
